"use client";

import { z } from "zod";
import parsePhoneNumberFromString from "libphonenumber-js";
import { IHubSpotForm } from "@/sanity/globals/HubspotForm";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shadcn/ui/form";
import { Input } from "@/shadcn/ui/input";
import { Button } from "@/shadcn/ui/button";
import { CornerButton } from "@/shadcn/ui/corner-button";
import { stegaClean } from "@sanity/client/stega";
import { submitFormDataToHubSpot } from "@/helpers/hubspot";
import { usePostHog } from "posthog-js/react";
import { sendGTMEvent } from "@next/third-parties/google";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";

export default function HubSpotForm({
  formData,
}: {
  formData: IHubSpotForm;
}) {
  const postHog = usePostHog();
  const [loading, setLoading] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(
    null
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const router = useRouter();

  const cleanFormData = useMemo(() => {
    return stegaClean(formData);
  }, [formData]);

  const formFields = useMemo(() => {
    return cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]:
          item.type === "text"
            ? item.required
              ? z.string().min(1, { message: "Erforderlich" })
              : z.string()
            : item.type === "email"
              ? item.required
                ? z.string().min(1, { message: "Erforderlich" }).email({ message: "Ungültige E-Mail" })
                : z.string().email({ message: "Ungültige E-Mail" }).optional()
              : item.type === "phone"
                ? z.string().transform((arg, ctx) => {
                    const phone = parsePhoneNumberFromString(arg, {
                      defaultCountry: "DE",
                      extract: false,
                    });

                    if (phone && phone.isValid()) {
                      return phone.number;
                    }

                    if (
                      (arg === "" || arg === null || arg === undefined) &&
                      !item.required
                    ) {
                      return arg;
                    }

                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: item.required ? "Erforderlich" : "Ungültige Telefonnummer",
                    });

                    return z.NEVER;
                  })
                : item.type === "number"
                  ? item.required
                    ? z.coerce.number({
                        invalid_type_error: "Ungültige Zahl",
                      }).min(0, { message: "Zahl muss positiv sein" })
                    : z.coerce.number({
                        invalid_type_error: "Ungültige Zahl",
                      }).min(0, { message: "Zahl muss positiv sein" }).optional()
                  : z.unknown(),
      };
    }, {});
  }, [cleanFormData]);

  const formSchema = useMemo(() => {
    return z.object(formFields);
  }, [formFields]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: cleanFormData?.fields?.reduce((obj, item) => {
      return {
        ...obj,
        [item.name]: "",
      };
    }, {}),
  });

  async function onSubmit(fieldData: any) {
    if (cleanFormData && fieldData) {
      setLoading(true);
      if (fieldData.email) {
        postHog.identify(fieldData.email);
      }
      postHog.capture("form_submit", {
        form: cleanFormData._id,
        formTitle: cleanFormData.name,
        fields: fieldData,
      });
      const res = await submitFormDataToHubSpot(
        fieldData,
        formData,
        window?.location?.href,
        document?.title
      );
      if (res?.success === true) {
        sendGTMEvent({
          event: "hsFormSubmit",
          "hs-form-guid": cleanFormData?.settings?.formId ?? null,
          pageName: document.title,
          pagePath: window.location.href,
          // @ts-ignore
          "form-firstname": formData?.firstName,
          // @ts-ignore
          "form-email": formData?.email,
        });

        if (cleanFormData?.settings?.onSubmit === "message") {
          setSuccessMessage(
            cleanFormData?.settings?.message ?? "Form submitted successfully"
          );
        } else if (cleanFormData?.settings?.onSubmit === "redirect") {
          const redirectUrl = resolvePageLink(
            cleanFormData?.settings?.redirect ?? null
          );
          if (redirectUrl) {
            router.push(redirectUrl);
          } else {
            setSuccessMessage(
              `Form submitted successfully. Could not redirect to page ${redirectUrl}`
            );
          }
        }
      } else {
        setLoading(false);
        setErrorMessage("Form submission failed. Try again later.");
      }
    }
  }

  return (
    <>
      {cleanFormData?.fields ? (
        <Form {...form}>
          <form
            className="w-full flex flex-col justify-start items-stretch relative max-w-full gap-[1.5rem]"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {cleanFormData?.fields?.map((formField, index) => {
              return (
                <FormField
                  key={index}
                  control={form.control}
                  // @ts-ignore - this is a valid prop
                  name={formField.name}
                  disabled={loading}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{formField.label}</FormLabel>
                      <FormControl>
                        {
                          // @ts-ignore - this is a valid prop
                          <Input
                            type={formField.type === "number" ? "number" : formField.type}
                            min={formField.type === "number" ? 0 : undefined}
                            placeholder={formField.placeholder}
                            {...field}
                            value={undefined}
                          />
                        }
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })}
            <div className="relative z-10 w-full">
              <Button
                type="submit"
                disabled={loading}
                className="relative z-10 w-full group flex gap-[1.25rem] items-center overflow-clip"
                variant="default"
                size="default"
                shape="round"
              >
                <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 group-hover:-translate-y-full button-bg" />
                <div className="absolute top-0 left-0 w-full h-full transition-transform duration-500 translate-y-full group-hover:translate-y-0 button-bg-hover" />
                <div className="button-text relative overflow-clip">
                  <div className="button-text-initial transition-all duration-300">
                    {formData?.settings?.submitText ?? "Submit"}
                  </div>
                  <div className="button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
                    {formData?.settings?.submitText ?? "Submit"}
                  </div>
                </div>
                <div className="button-icon relative !w-[3rem] !min-w-[3rem] !h-[3rem] [&_svg]:w-full [&_svg]:shrink-0 overflow-clip flex items-center justify-center">
                  <div className="button-icon-initial transition-all duration-300 w-full h-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <rect width="20" height="20" fill="none" fillOpacity="0.01" />
                      <path
                        d="M13.75 10L7.5 16.25L6.625 15.375L12 10L6.625 4.625L7.5 3.75L13.75 10Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                  <div className="button-icon-hover absolute left-[-3rem] !w-[3rem] !min-w-[3rem] !h-[3rem] [&_svg]:w-full [&_svg]:shrink-0 transition-all duration-300 pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="100%"
                      height="100%"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <rect width="20" height="20" fill="none" fillOpacity="0.01" />
                      <path
                        d="M13.75 10L7.5 16.25L6.625 15.375L12 10L6.625 4.625L7.5 3.75L13.75 10Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </div>
              </Button>
            </div>
            {successMessage && (
              <div className="w-full bg-green-100 border-green-500 border rounded-sm font-bold text-green-900 py-[1rem] px-[2rem] drop-shadow-md">
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className="w-full bg-red-100 border-red-500 border rounded-sm font-bold text-red-900 py-[1rem] px-[2rem] drop-shadow-md">
                {errorMessage}
              </div>
            )}
          </form>
        </Form>
      ) : (
        <div className="bg-gray-100 border-black border-2 rounded-xl font-bold text-blue-900 p-20">
          MISSING FORM DATA
        </div>
      )}
    </>
  );
}
